
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import DnsInstructions from './DnsInstructions';

interface Domain {
  id: string;
  domain: string;
  status: 'pending' | 'verified' | 'active';
  created_at: string;
}

interface DomainItemProps {
  domain: Domain;
  getStatusBadge: (status: string) => React.ReactNode;
  verifyDomain: (domainId: string, domain: string) => Promise<void>;
  verifyDomainManually: (domainId: string) => Promise<void>;
  deleteDomain: (domainId: string) => Promise<void>;
  isVerifying: string | null;
  isDeleting: string | null;
  verificationError: string | null;
  dnsCheckResult: any;
  manualVerification: boolean;
}

export const DomainItem: React.FC<DomainItemProps> = ({
  domain,
  getStatusBadge,
  verifyDomain,
  verifyDomainManually,
  deleteDomain,
  isVerifying,
  isDeleting,
  verificationError,
  dnsCheckResult,
  manualVerification,
}) => {
  return (
    <div key={domain.id} className="border rounded-md p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="font-medium">{domain.domain}</h3>
          {getStatusBadge(domain.status)}
        </div>
        <div className="flex items-center gap-2">
          {domain.status !== 'active' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => verifyDomain(domain.id, domain.domain)}
              disabled={isVerifying === domain.id}
            >
              {isVerifying === domain.id ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Vérifier
            </Button>
          )}
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-red-500 border-red-200">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                <AlertDialogDescription>
                  Êtes-vous sûr de vouloir supprimer le domaine "{domain.domain}" ?
                  Cette action ne peut pas être annulée.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => deleteDomain(domain.id)}
                  className="bg-red-500 hover:bg-red-600"
                >
                  {isDeleting === domain.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Supprimer"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      
      <div className="text-sm text-gray-500">
        <DnsInstructions 
          domain={domain.domain}
          status={domain.status}
          domainId={domain.id}
          isVerifying={isVerifying}
          verificationError={verificationError}
          dnsCheckResult={dnsCheckResult}
          manualVerification={manualVerification}
          verifyDomainManually={verifyDomainManually}
        />
      </div>
    </div>
  );
};

export default DomainItem;
